/// Axis-aligned hitboxes, the only collision shape the game uses.
///
/// Flame's RectangleHitbox is a child component positioned inside its parent,
/// and detection is a broad AABB overlap. Both are reproduced here directly:
/// no rotation means the absolute rect is just a parent-chain walk.

import { Component, PositionComponent } from './component';
import type { Vec2 } from './vector';

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export class RectangleHitbox extends PositionComponent {
  constructor(options: { size: Vec2; position?: Vec2 }) {
    super({ size: options.size, position: options.position ?? { x: 0, y: 0 } });
  }

  /**
   * Sized as a fraction of the parent, matching RectangleHitbox.relative.
   * `position` stays an absolute offset in parent-local pixels, as in Dart.
   */
  static relative(factor: number, parentSize: Vec2, position?: Vec2): RectangleHitbox {
    return new RectangleHitbox({
      size: { x: parentSize.x * factor, y: parentSize.y * factor },
      position: position ?? { x: 0, y: 0 },
    });
  }

  toRect(): Rect {
    const origin = this.absoluteOrigin();
    return {
      left: origin.x,
      top: origin.y,
      right: origin.x + this.size.x,
      bottom: origin.y + this.size.y,
    };
  }
}

export function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

/** Any hitbox belonging to this component or its descendants. */
export function hitboxesOf(component: Component): RectangleHitbox[] {
  return component.descendantsOfType(RectangleHitbox);
}

export function collides(a: Component, b: Component): boolean {
  const aBoxes = hitboxesOf(a);
  const bBoxes = hitboxesOf(b);

  for (const aBox of aBoxes) {
    const aRect = aBox.toRect();
    for (const bBox of bBoxes) {
      if (rectsOverlap(aRect, bBox.toRect())) return true;
    }
  }
  return false;
}
